import type { ReactNode } from 'react';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled, { css } from 'styled-components';
import { Avatar } from '../Avatar';
import { useRevealedText } from './useRevealedText';
import { colors, radii, spacing, typography } from '../../theme-v2';

export type ChatMessageRole = 'user' | 'assistant';

export interface ChatMessageAvatar {
  /** Image URL. Falls back to initials from `name` when absent. */
  src?: string;
  /** Display name; used to derive initials when `src` is missing. */
  name?: string;
}

export interface ChatMessageProps {
  /** Who sent the message. Drives alignment + bubble color. */
  role: ChatMessageRole;
  /** Message body — plain string or pre-rendered markdown/JSX. */
  children: ReactNode;
  /** Optional avatar (only shown on assistant messages). */
  avatar?: ChatMessageAvatar;
  /** Pre-formatted timestamp (consumer formats — locale-aware). */
  timestamp?: string;
  /** When true, renders a typing-indicator placeholder instead of `children`. */
  pending?: boolean;
  /** When true, string `children` are rendered as Markdown (GFM, raw HTML disabled). */
  markdown?: boolean;
  /**
   * When true, the answer is still streaming: string `children` are revealed
   * progressively (typewriter). Off by default; ignored for non-string children.
   */
  streaming?: boolean;
  className?: string;
}

/**
 * ChatMessage — a single message bubble in the Chatbot thread.
 * Right-aligned + accent-colored for `role="user"`, left-aligned +
 * surface-muted with an optional avatar for `role="assistant"`.
 *
 * Stateless. Timestamps are pre-formatted by the caller so this
 * component stays locale-agnostic.
 */
export const ChatMessage = ({
  role,
  children,
  avatar,
  timestamp,
  pending,
  markdown,
  streaming,
  className,
}: ChatMessageProps) => {
  const showAvatar = role === 'assistant' && avatar;
  const isMarkdown = markdown === true && typeof children === 'string';
  const text = typeof children === 'string' ? children : '';
  const revealed = useRevealedText(text, streaming === true);
  // Screen readers announce the bubble once, when it's complete, not per frame.
  const busy = streaming === true || revealed.length < text.length;

  return (
    <StyledRow $role={role} className={className}>
      {showAvatar && (
        <StyledAvatarSlot>
          <Avatar src={avatar.src} name={avatar.name} size="sm" />
        </StyledAvatarSlot>
      )}
      <StyledColumn $role={role}>
        <StyledBubble
          $role={role}
          $markdown={isMarkdown}
          aria-busy={busy || undefined}
        >
          {pending ? (
            <TypingIndicator />
          ) : isMarkdown ? (
            <Markdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {/* Legacy parity: the backend sends <br> for line breaks. */}
              {revealed.replace(/<br\/?>/gi, '\n')}
            </Markdown>
          ) : (
            children
          )}
        </StyledBubble>
        {timestamp && <StyledTimestamp>{timestamp}</StyledTimestamp>}
      </StyledColumn>
    </StyledRow>
  );
};

// Links open in a new tab so a click doesn't navigate away from the experience.
const markdownComponents: Components = {
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

const TypingIndicator = () => (
  <StyledTyping aria-hidden="true">
    <span />
    <span />
    <span />
  </StyledTyping>
);

const StyledRow = styled.div<{ $role: ChatMessageRole }>`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.sm}px;
  flex-direction: ${({ $role }) => ($role === 'user' ? 'row-reverse' : 'row')};
  max-width: 100%;
`;

const StyledAvatarSlot = styled.div`
  flex-shrink: 0;
  margin-top: ${spacing.xs}px;
`;

const StyledColumn = styled.div<{ $role: ChatMessageRole }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  gap: ${spacing.xs}px;
  max-width: 80%;
  min-width: 0;
`;

const userBubble = css`
  background: ${colors.accent.base};
  color: ${colors.text.onAccent};
  border-bottom-right-radius: ${radii.xs}px;
`;

const assistantBubble = css`
  background: ${colors.surface.muted};
  color: ${colors.text.primary};
  border-bottom-left-radius: ${radii.xs}px;
`;

// Tight margins: loose lists wrap items in <p>, which would otherwise add blank lines.
// `normal` on the bubble drops the newlines react-markdown puts between blocks;
// text blocks keep `pre-wrap` so a single line break still starts a new line.
const markdownContent = css`
  white-space: normal;
  max-width: 100%;

  p,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  td,
  th {
    white-space: pre-wrap;
  }

  p,
  ul,
  ol,
  pre,
  table {
    margin: 0 0 ${spacing.sm}px;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0 0 ${spacing.sm}px;
    font-size: inherit;
    font-weight: ${typography.weight.semibold};
  }
  /* Wide GFM tables and code blocks scroll inside the bubble, not the chat panel. */
  pre,
  table {
    display: block;
    max-width: 100%;
    overflow-x: auto;
  }
  ul,
  ol {
    padding-left: ${spacing.lg}px;
  }
  li > p {
    margin: 0;
  }
  li + li {
    margin-top: ${spacing.xs}px;
  }
  > :last-child {
    margin-bottom: 0;
  }
  strong {
    font-weight: ${typography.weight.semibold};
  }
  a {
    color: ${colors.accent.base};
    text-decoration: underline;
  }
`;

const StyledBubble = styled.div<{
  $role: ChatMessageRole;
  $markdown: boolean;
}>`
  padding: ${spacing.sm}px ${spacing.md}px;
  border-radius: ${radii.box}px;
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.md}px;
  line-height: ${typography.lineHeight.body};
  white-space: pre-wrap;
  word-break: break-word;

  ${({ $role }) => ($role === 'user' ? userBubble : assistantBubble)}
  ${({ $markdown }) => ($markdown ? markdownContent : null)}
`;

const StyledTimestamp = styled.span`
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.xs}px;
  color: ${colors.text.tertiary};
  padding: 0 ${spacing.xs}px;
`;

const StyledTyping = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: ${spacing.xs}px 0;

  > span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${colors.text.tertiary};
    animation: typing-bounce 1.4s infinite ease-in-out;
  }
  > span:nth-child(2) {
    animation-delay: 0.2s;
  }
  > span:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes typing-bounce {
    0%,
    60%,
    100% {
      transform: translateY(0);
      opacity: 0.4;
    }
    30% {
      transform: translateY(-4px);
      opacity: 1;
    }
  }
`;
